import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ProcessMenu } from '../components/ProcessMenu.js';
import { SecurityProcessMenu } from '../components/SecurityProcessMenu.js';
import {
  PROCESS_MENU_OPTIONS,
  makeProcessMenuHandler,
} from './process-menu-shared.js';
import { toast } from 'sonner';
import { api } from '../api/client.js';
import { useCustomer } from '../customer-context.js';
import { accountOptionLabel } from '../lib/account-label.js';

/**
 * SCRN 14 — Customer Notes & Remarks — PERMANENT notes (CCF-49, view-only) plus
 * temporary remarks (CCF-50 view, CCF-56 add, CCF-51 delete).
 *
 * Issue #237 (relaxes #222 for this screen only): @dylan219 (PTSB product
 * authority) ruled on 2026-07-01 that the agent must reach Customer Notes and
 * perform ALL actions EVEN when the customer cannot complete medium security —
 * the exact scenario is "customer failed medium, leave a note that they've been
 * referred to branch". So this screen no longer shows the #222
 * `ServicingVerificationRequired` panel; it works at the IDENTIFIED stage (a
 * bound CIF from ID&V card lookup). Enforcement stays server-side: the notes +
 * notes-accounts routes are gated by the BFF's lighter `requireIdentified` gate
 * (an identified/bound customer) instead of medium `requireVerified`.
 *
 * Faithful port of the customer's Figma "Customer notes" design onto the
 * Accounts-domain temporary-remarks gateway contract (R4 v260626 remarks/list,
 * remarks/add, remarks/delete). The agent picks "Temporary Notes" from the
 * note-options dropdown, then an account (name + last 4, CCF-80 masking); the
 * screen shows the account's remarks (Details / Account Remarks / Select), with
 * a Delete for the ticked block(s) (CCF-51 — one checkbox per block, deleted as
 * one unit) and an Add Remark flow (up to 10 lines, ≤40 chars each — CCF-56).
 *
 * Permanent notes (CCF-49) are CIF-scoped, read-only free text from the legacy
 * Unibanks screen 49 (returned "as-is"), grouped on screen by ReferenceNumber.
 * The agent picks "Permanent Notes" from the note-options dropdown to view them;
 * there is no add/edit (the R4 `customer/note` op is view-only). Issue #330 /
 * tester report #298 wired this to the real gateway op (the loader was a stub
 * that always rendered the empty state). UNLIKE the temporary-notes routes,
 * permanent notes are NOT part of the #237 identified-stage relaxation: CCF-49
 * requires ID&V + Medium Security to view permanent notes/complaint records, so
 * the BFF gates this read with medium `requireVerified` (a 403 → the agent is
 * told to complete medium security first).
 *
 * Data comes from the Teller-ID-gated BFF via the generated typed client:
 *   - permanent notes: POST /api/customers/permanent-notes (CCF-49, medium-verified)
 *   - accounts: POST /api/accounts/notes-accounts  (issue #237 — identified-only)
 *   - remarks:  POST /api/accounts/remarks
 *   - add:      POST /api/accounts/remark-add
 *   - delete:   POST /api/accounts/remark-delete
 *
 * The shared agent banner ("NavDark") lives in the app shell (AppLayout), so this
 * screen renders only the `<main>` content.
 */

/** A single account row (open contract shape; the selector renders a subset). */
interface AccountRow {
  AccountNumber: string;
  AccountType: string;
  Nickname?: string;
}

/** A single temporary remark block (CCF-50). */
interface RemarkRow {
  Id?: string;
  Details?: string;
  Lines?: string[];
  FinDate?: string;
  Time?: string;
}

/** The remarks on an account (CCF-50). */
interface RemarkList {
  Found?: boolean;
  AccountNumber?: string;
  Remarks?: RemarkRow[];
  /** Gateway failure reason when Found:false (e.g. invalid account). */
  ResultCode?: string;
  ResultMessage?: string;
}

/**
 * A single permanent customer note (CCF-49). Read-only, free-text from the
 * legacy Unibanks screen 49; returned "as-is". Lines that share a
 * `ReferenceNumber` are grouped together on screen. `NoteType` is one of the
 * four permanent note types (0001 General / 0701 Complaint-Open / 0702
 * Complaint-Closed / 0703 Complaint-Closed-in-Error).
 */
interface PermanentNote {
  ReferenceNumber?: string;
  NoteType?: string;
  NoteText?: string;
  NoteDate?: string;
}

/**
 * Human-readable headings for the four permanent note-type codes (CCF-49).
 * Sourced from the Unibanks `Unibanks_GetCustomerNotes.yaml` spec and the
 * Unibanks screen 49 display (issue #630). Rendered as section headings above
 * each note-type group so agents see the same category labels as in Unibanks.
 */
const NOTE_TYPE_LABEL: Record<string, string> = {
  '0001': 'CUSTOMER NOTES',
  '0701': 'CUSTOMER COMPLAINT OPEN',
  '0702': 'CUSTOMER COMPLAINT CLOSED',
  '0703': 'CUSTOMER COMPLAINT CLOSED IN ERROR',
};

/**
 * Permanent note-type FILTER options (issue #630, @philipc39 2026-08-13). A
 * second dropdown beside "Note options" lets the agent narrow the permanent
 * notes to a single Unibanks category. The empty value is the default "All"
 * (show every type — the page-load behaviour); each other value maps a filter
 * label to the underlying gateway `NoteType` code so the client-side filter can
 * match on the raw code.
 */
const NOTE_TYPE_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All note types' },
  { value: '0001', label: 'CUSTOMER NOTES' },
  { value: '0701', label: 'CUSTOMER COMPLAINT OPEN' },
  { value: '0702', label: 'CUSTOMER COMPLAINT CLOSED' },
  { value: '0703', label: 'CUSTOMER COMPLAINT CLOSED IN ERROR' },
];

/** Maximum remark blocks per account (CCF-56). */
const REMARK_LIMIT = 10;
/** Maximum characters per remark line (CCF-56). */
const REMARK_LINE_MAX = 40;
/** Maximum lines per remark (CCF-56). */
const REMARK_LINE_LIMIT = 10;

/**
 * Auto-wrap remark text so no visual line exceeds {@link REMARK_LINE_MAX}
 * characters (CCF-56 / issue #258). PTSB temporary remarks are a fixed-width
 * multi-line model (≤10 lines × ≤40 chars); the agent should be able to keep
 * typing past 40 characters and have the overflow flow onto the next line
 * automatically, rather than being blocked or erroring on submit.
 *
 * Behaviour: existing hard line breaks (the agent's own Enter presses) are
 * preserved; each such segment is then greedily wrapped at word boundaries
 * where possible, hard-breaking only a single word that is itself longer than
 * the line limit. Idempotent — re-wrapping already-wrapped text only ever
 * collapses an over-long line, so editing mid-text stays sane.
 */
export function wrapRemarkLines(text: string): string {
  const out: string[] = [];
  for (const segment of text.split('\n')) {
    if (segment.length <= REMARK_LINE_MAX) {
      out.push(segment);
      continue;
    }
    let current = '';
    for (const word of segment.split(' ')) {
      if (word.length > REMARK_LINE_MAX) {
        // A single word longer than the limit: flush, then hard-chop it.
        if (current) {
          out.push(current);
          current = '';
        }
        let rest = word;
        while (rest.length > REMARK_LINE_MAX) {
          out.push(rest.slice(0, REMARK_LINE_MAX));
          rest = rest.slice(REMARK_LINE_MAX);
        }
        current = rest;
        continue;
      }
      const candidate = current ? `${current} ${word}` : word;
      if (candidate.length > REMARK_LINE_MAX) {
        out.push(current);
        current = word;
      } else {
        current = candidate;
      }
    }
    out.push(current);
  }
  return out.join('\n');
}

/**
 * Format a failed remark action (Add/Delete) for the INLINE error shown beside
 * the action button (authoritative inline-error UX, #80/#82/#94/#129): surface
 * the gateway's own Code + Message verbatim, falling back to a clear retryable
 * message. A `0000` code is treated as no code. Mirrors NoticeInformationScreen.
 */
function formatActionError(
  result: { ResultCode?: string; ResultMessage?: string } | undefined,
  fallback: string,
): string {
  const code = result?.ResultCode?.trim();
  const message = result?.ResultMessage?.trim();
  const showCode = code && code !== '0000';
  if (showCode && message) return `${code} — ${message}`;
  if (message) return message;
  if (showCode) return `${fallback} (code ${code}).`;
  return fallback;
}

/** Accessible dropdown, faithful to the export's listbox pattern. */
function Dropdown({
  id,
  label,
  value,
  placeholder,
  options,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  placeholder: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    function onClickOutside(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    }
    if (open) {
      document.addEventListener('mousedown', onClickOutside);
      return () => document.removeEventListener('mousedown', onClickOutside);
    }
    return undefined;
  }, [open]);

  return (
    <div className="flex flex-col gap-2" ref={ref}>
      <label
        htmlFor={id}
        className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
      >
        {label}
      </label>
      <div className="relative">
        <button
          id={id}
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
          className="w-full px-4 py-3 border-2 border-[#dad7d3] rounded-md font-['Arial',sans-serif] text-[16px] text-[#1d252c] bg-white text-left transition-all focus:outline-none focus:border-[#005fcc] focus:ring-4 focus:ring-[#005fcc]/20 hover:border-[#979c9f] flex items-center justify-between"
        >
          <span className={selected ? '' : 'text-[#6b7280]'}>
            {selected ? selected.label : placeholder}
          </span>
          <svg
            className={`size-5 transition-transform ${open ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
        {open && (
          <ul
            role="listbox"
            aria-label={label}
            className="absolute top-full mt-2 w-full bg-white border-2 border-[#dad7d3] rounded-md shadow-lg z-[100] max-h-[240px] overflow-y-auto"
          >
            {options.map((option) => (
              <li
                key={option.value || 'placeholder'}
                role="option"
                aria-selected={value === option.value}
                tabIndex={0}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onChange(option.value);
                    setOpen(false);
                  }
                }}
                className={`px-4 py-3 cursor-pointer font-['Arial',sans-serif] text-[16px] text-[#1d252c] hover:bg-[#f3f4f6] focus:outline-none focus:bg-[#e5e7eb] ${
                  value === option.value ? 'bg-[#e5e7eb] font-bold' : ''
                }`}
              >
                {option.label}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export function CustomerNotesScreen(): React.JSX.Element {
  const { customer } = useCustomer();
  const navigate = useNavigate();
  const location = useLocation();

  // Issue #663 (CCRS-3448): whether this screen was reached FROM a security
  // page's "Customer Notes" cross-nav entry, and which security page to return
  // to. Set once at mount from the navigation state — a security page's process
  // menu sets `{ fromSecurity: true, returnTo, securityScreenId, securityMode }`
  // (see SecurityProcessMenu). In that flow the Customer Notes screen presents
  // the SAME security process menu (so the agent can swivel back into the
  // security flow) instead of the default servicing process menu, and offers a
  // "Back to security" exit to the originating page. Any OTHER entry (the
  // servicing process menu, direct nav) leaves it null and the servicing process
  // menu is shown unchanged. Mirrors #454's Hold-Information round trip.
  const locationState = location.state as {
    fromSecurity?: boolean;
    returnTo?: string;
    securityScreenId?: string;
    securityMode?: 'medium' | 'high';
  } | null;
  const securityReturn = useRef<{
    returnTo: string;
    screenId: string;
    mode: 'medium' | 'high';
  } | null>(
    locationState?.fromSecurity && locationState.returnTo
      ? {
          returnTo: locationState.returnTo,
          screenId: locationState.securityScreenId ?? '',
          mode: locationState.securityMode ?? 'medium',
        }
      : null,
  );
  const fromSecurity = !!securityReturn.current;

  const customerNumber = customer.cif ?? '';
  const tellerId = customer.tellerId ?? '';
  const hasContext = !!customerNumber && /^\d{4}$/.test(tellerId);

  // Issue #593: the "Back to customer dashboard" affordance is only valid once
  // the customer is Medium-verified — the Dashboard route is `requireVerified`
  // (medium), so from the IDENTIFIED stage (Notes reached via a security screen
  // pre-Medium, the deliberate #237 exception) that link lands on the "no
  // security verification" error boundary. Only offer it when the agent has
  // actually cleared Medium (or High); otherwise the "Back to ID&V" affordance
  // (#371) is the valid way back. `securityLevel` is the client-side signal that
  // Medium Security completed (see session.ts).
  const dashboardVerified =
    customer.securityLevel === 'medium' || customer.securityLevel === 'high';

  // Shared process-menu navigation handler (issue #45).
  const onProcessSelect = makeProcessMenuHandler(navigate);

  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  // Note options (CCF-49/50): "Permanent Notes" (CIF-scoped, read-only —
  // CCF-49) and "Temporary Notes" (account-scoped remarks — CCF-50). The
  // screen reveals the relevant view once an option is chosen.
  const [noteOption, setNoteOption] = useState('');
  // Permanent notes (CCF-49): grouped by ReferenceNumber, read-only. Null until
  // loaded; empty array = "No permanent comments are on file".
  const [permanentNotes, setPermanentNotes] = useState<PermanentNote[] | null>(
    null,
  );
  // Permanent note-type filter (issue #630, @philipc39): '' = show all types
  // (the default page-load view); a NoteType code narrows to that type only.
  const [permanentTypeFilter, setPermanentTypeFilter] = useState('');
  const [selectedAccount, setSelectedAccount] = useState('');
  const [remarks, setRemarks] = useState<RemarkList | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Selected remark blocks (by Id) to delete (CCF-51).
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Per-action delete failure shown inline beneath the table (#129).
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Add-remark form state (CCF-56).
  const [showAdd, setShowAdd] = useState(false);
  const [addText, setAddText] = useState('');
  const [addFieldError, setAddFieldError] = useState('');
  // Gateway/add failure (Code + Message) shown INLINE beside Complete (#129).
  const [addError, setAddError] = useState('');
  // Surfaced when the agent clicks Add Remark at the 10-block limit (CCF-56).
  const [limitError, setLimitError] = useState('');
  const [adding, setAdding] = useState(false);

  // Load the customer's accounts up front.
  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      if (!hasContext) {
        // Issue #237: notes work at the IDENTIFIED stage (a bound CIF from ID&V
        // card lookup), so this only fires when NO customer is identified at all
        // — a helpful message, never a crash.
        setError('No customer identified. Complete ID&V (card lookup) first.');
        return;
      }
      try {
        const {
          data,
          error: apiError,
          response,
        } = await api.POST('/accounts/notes-accounts', {
          params: { header: { 'X-Teller-Id': tellerId } },
          body: { customerNumber },
        });
        if (cancelled) return;
        if (response.status === 403) {
          // Issue #237: the notes-accounts route requires only an identified
          // customer. A 403 here means the interaction has no bound customer
          // (or a customer mismatch) — surface a helpful, non-crashing message.
          setError(
            'No customer identified. Complete ID&V (card lookup) first.',
          );
          return;
        }
        if (apiError || !data) {
          setError('Could not load accounts.');
          return;
        }
        setAccounts((data.Accounts ?? []) as unknown as AccountRow[]);
      } catch {
        if (!cancelled) setError('Request failed.');
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerNumber, tellerId]);

  const remarkBlocks = remarks?.Remarks ?? [];
  const atLimit = remarkBlocks.length >= REMARK_LIMIT;

  async function loadRemarks(accountNumber: string): Promise<void> {
    if (!accountNumber) return;
    setLoading(true);
    setError('');
    setRemarks(null);
    setSelected(new Set());
    setDeleteError('');
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/accounts/remarks', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: { customerNumber, accountNumber },
      });
      if (apiError || !data) {
        setError(
          response.status === 403
            ? 'Forbidden: check your Teller ID and permissions.'
            : `Could not load remarks (${response.status}).`,
        );
        return;
      }
      const result = data as unknown as RemarkList;
      // A gateway failure (`Found:false`, e.g. invalid/unlinked account) is shown
      // inline rather than as an empty list (issue #120 class).
      if (result.Found === false) {
        setError(
          result.ResultMessage
            ? `Remarks unavailable for this account: ${result.ResultMessage}`
            : 'Remarks are not available for the selected account.',
        );
        return;
      }
      setRemarks(result);
    } catch {
      setError('Request failed.');
    } finally {
      setLoading(false);
    }
  }

  /**
   * Load the customer's permanent notes (CCF-49) from the Teller-ID-gated BFF
   * (`POST /api/customers/permanent-notes`), which maps to the R4 `customer/note`
   * gateway op — one call per `CustomerNoteType` (0001/0701/0702/0703),
   * concatenated. View-only (the op has no update function). An empty result
   * renders the CCF-49 "No permanent comments are on file" state; the rows are
   * grouped by `ReferenceNumber` for display (issue #330 / tester report #298).
   */
  async function loadPermanentNotes(): Promise<void> {
    setError('');
    setLoading(true);
    setPermanentNotes(null);
    if (!hasContext) {
      setError('No customer identified. Complete ID&V (card lookup) first.');
      setLoading(false);
      return;
    }
    // CCF-49: permanent notes require medium security server-side. Short-circuit
    // client-side when the level is known to be below medium so the agent gets
    // immediate feedback without a 403 round-trip. The BFF gate is still the
    // authoritative enforcement; the 403 catch below remains as defence-in-depth.
    const LEVEL_ORDER = { none: 0, pan: 1, medium: 2, high: 3 };
    if (
      (LEVEL_ORDER[customer.securityLevel ?? 'none'] ?? 0) < LEVEL_ORDER.medium
    ) {
      setError(
        'Complete customer ID&V and medium security verification before viewing permanent notes.',
      );
      setLoading(false);
      return;
    }
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/customers/permanent-notes', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: { customerNumber },
      });
      if (response.status === 403) {
        // CCF-49: permanent notes require ID&V + Medium Security. A 403 is either
        // "no customer identified" or "medium verification not completed" — both
        // resolved by completing the security flow, so guide the agent there.
        setError(
          'Complete customer ID&V and medium security verification before viewing permanent notes.',
        );
        return;
      }
      if (apiError || !data) {
        setError(`Could not load permanent notes (${response.status}).`);
        return;
      }
      setPermanentNotes(
        (data.PermanentNotes ?? []) as unknown as PermanentNote[],
      );
    } catch {
      setError('Request failed.');
    } finally {
      setLoading(false);
    }
  }

  function onAccountChange(value: string): void {
    setSelectedAccount(value);
    setShowAdd(false);
    setAddText('');
    setAddFieldError('');
    setAddError('');
    setLimitError('');
    void loadRemarks(value);
  }

  function toggleSelected(id: string): void {
    setDeleteError('');
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function deleteSelected(): Promise<void> {
    const ids = [...selected];
    if (ids.length === 0) return;
    setDeleting(true);
    setDeleteError('');
    try {
      // Delete each selected block; the listing reflects all on refresh (CCF-51).
      for (const id of ids) {
        const {
          data,
          error: apiError,
          response,
        } = await api.POST('/accounts/remark-delete', {
          params: { header: { 'X-Teller-Id': tellerId } },
          body: { customerNumber, accountNumber: selectedAccount, id },
        });
        if (
          apiError ||
          !data ||
          (data as { Success?: boolean }).Success !== true
        ) {
          setDeleteError(
            response.status === 403
              ? 'Forbidden: check your Teller ID and permissions.'
              : formatActionError(
                  data as
                    | { ResultCode?: string; ResultMessage?: string }
                    | undefined,
                  'Could not delete the remark. Please retry.',
                ),
          );
          return;
        }
      }
      toast.success('Remark deleted successfully.');
      setSelected(new Set());
      await loadRemarks(selectedAccount);
    } catch {
      setDeleteError('Could not delete the remark. Please retry.');
    } finally {
      setDeleting(false);
    }
  }

  async function submitAdd(): Promise<void> {
    setAddError('');
    // Split into lines, drop trailing blanks, validate against CCF-56 limits.
    const lines = addText
      .split('\n')
      .map((l) => l.trimEnd())
      .filter((l) => l.length > 0);
    if (lines.length === 0) {
      setAddFieldError('Enter at least one remark line.');
      return;
    }
    if (lines.length > REMARK_LINE_LIMIT) {
      setAddFieldError(
        `A remark can have at most ${REMARK_LINE_LIMIT} lines. Delete an existing remark to make space.`,
      );
      return;
    }
    if (lines.some((l) => l.length > REMARK_LINE_MAX)) {
      setAddFieldError(
        `Each line can be at most ${REMARK_LINE_MAX} characters — use additional lines.`,
      );
      return;
    }
    setAdding(true);
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/accounts/remark-add', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: { customerNumber, accountNumber: selectedAccount, lines },
      });
      if (
        apiError ||
        !data ||
        (data as { Success?: boolean }).Success !== true
      ) {
        setAddError(
          response.status === 403
            ? 'Forbidden: check your Teller ID and permissions.'
            : formatActionError(
                data as
                  | { ResultCode?: string; ResultMessage?: string }
                  | undefined,
                'Could not add the remark. Please retry.',
              ),
        );
        return;
      }
      toast.success('Remark added successfully.');
      setShowAdd(false);
      setAddText('');
      setAddFieldError('');
      setAddError('');
      await loadRemarks(selectedAccount);
    } catch {
      setAddError('Could not add the remark. Please retry.');
    } finally {
      setAdding(false);
    }
  }

  const showTemporary = noteOption === 'temporary';
  const showPermanent = noteOption === 'permanent';

  // CCF-49 / issue #630 / issue #642 — group permanent notes by NoteType first
  // (so the Unibanks section headings appear), then by NOTE NUMBER within each
  // type. Preserves the ascending order the gateway returns for the types
  // (0001 → 0701 → 0702 → 0703) and for the note numbers within each type.
  //
  // Issue #642 (@agent 2026-08-14): the R4 `customer/note` ReferenceNumber is a
  // 10-char key — the **first 5 chars are the note number** (the identifier
  // that groups fragments together) and the **last 5 chars are the sequence**
  // (fragment order within the note). A single permanent note is stored as one
  // or more 80-char fragments that share a note number and differ only by
  // sequence (contract example: 0000300001/…02/…03 are one note; 0000400001
  // begins the next). Grouping on the WHOLE reference wrongly rendered each
  // fragment as a separate note (the 9560320381 case: 0000100001 + 0000100002
  // shown as two). Group on the note number and order fragments by sequence so
  // the fragments render as ONE note.
  //
  // Issue #630 (@philipc39 2026-08-13): the note-type filter dropdown narrows
  // the displayed notes to a single type client-side. An empty filter ('') is
  // the default "All" view (the page-load behaviour — show every type).
  const permanentGroups = React.useMemo(() => {
    // Two-level structure: NoteType → Map<noteNumber, PermanentNote[]>
    const byType = new Map<string, Map<string, PermanentNote[]>>();
    for (const note of permanentNotes ?? []) {
      const typeKey = note.NoteType ?? '';
      // Apply the note-type filter (issue #630): when a type is selected, only
      // notes of that NoteType are shown; the empty filter shows all types.
      if (permanentTypeFilter && typeKey !== permanentTypeFilter) continue;
      const ref = note.ReferenceNumber ?? '';
      // Note number = first 5 chars of the reference (issue #642). References
      // shorter than 5 chars (unexpected) fall back to the whole value.
      const noteNumber = ref.length >= 5 ? ref.slice(0, 5) : ref;
      let refMap = byType.get(typeKey);
      if (!refMap) {
        refMap = new Map<string, PermanentNote[]>();
        byType.set(typeKey, refMap);
      }
      const bucket = refMap.get(noteNumber);
      if (bucket) bucket.push(note);
      else refMap.set(noteNumber, [note]);
    }
    // Order fragments within each note by their sequence (last 5 chars of the
    // reference — zero-padded, so a lexicographic compare on the full reference
    // is equivalent). The gateway already returns them in order, but sort
    // defensively so display is stable regardless of arrival order (issue #642).
    for (const refMap of byType.values()) {
      for (const bucket of refMap.values()) {
        bucket.sort((a, b) =>
          (a.ReferenceNumber ?? '').localeCompare(b.ReferenceNumber ?? ''),
        );
      }
    }
    // Flatten to an array of { noteType, refGroups } for rendering. Each
    // refGroup is [noteNumber, fragments].
    return [...byType.entries()].map(([noteType, refMap]) => ({
      noteType,
      label: NOTE_TYPE_LABEL[noteType] ?? noteType,
      refGroups: [...refMap.entries()],
    }));
  }, [permanentNotes, permanentTypeFilter]);

  return (
    <main
      className="max-w-[1800px] mx-auto px-12 py-12"
      aria-labelledby="notes-heading"
    >
      {/* Back-navigation. Two affordances because Customer Notes is reachable
          from two entry points: the post-Medium customer Dashboard (full
          servicing context) AND — per issue #237 — the ID&V note-options
          dropdown at the IDENTIFIED stage (after O24 + PAN, before Medium
          Security). At the identified stage there is no full Dashboard to
          return to, so "Back to customer dashboard" alone would strand the
          agent (issue #371). "Back to ID&V" always returns to the working
          identity/verification screen. Both are offered so neither entry path
          is left without a valid way back.

          Issue #593: the "Back to customer dashboard" link is shown ONLY when
          the customer is Medium-verified (`dashboardVerified`). Pre-Medium (the
          #237 security-screen entry) the Dashboard route's `requireVerified`
          gate rejects, showing the "no security verification" error boundary —
          a dead end — so we suppress that link and leave only "Back to ID&V". */}
      <div className="mb-8 flex flex-wrap items-center gap-6">
        {/* Issue #663 (CCRS-3448): when reached from a security page, offer a
            "Back to security" exit to the exact originating security screen
            (mirrors #454's Account-Holds round trip), in addition to the
            standard affordances. `restoreSecurityRun` lets the security screen
            restore the in-progress verification run on return (as #454 does). */}
        {fromSecurity && (
          <a
            href={securityReturn.current!.returnTo}
            onClick={(e) => {
              e.preventDefault();
              navigate(securityReturn.current!.returnTo, {
                state: { restoreSecurityRun: true },
              });
            }}
            className="inline-flex items-center gap-2 text-[16px] font-['Arial',sans-serif] text-[#FF703E] underline hover:text-[#e55a2a] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 rounded"
          >
            &lt; Back to security
          </a>
        )}
        {dashboardVerified && (
          <a
            href="/dashboard"
            onClick={(e) => {
              e.preventDefault();
              navigate('/dashboard');
            }}
            className="inline-flex items-center gap-2 text-[16px] font-['Arial',sans-serif] text-[#FF703E] underline hover:text-[#e55a2a] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 rounded"
          >
            &lt; Back to customer dashboard
          </a>
        )}
        <a
          href="/idv"
          onClick={(e) => {
            e.preventDefault();
            navigate('/idv');
          }}
          aria-label="Back to ID&V"
          className="inline-flex items-center gap-2 text-[16px] font-['Arial',sans-serif] text-[#FF703E] underline hover:text-[#e55a2a] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 rounded"
        >
          &lt; Back to ID&amp;V
        </a>
      </div>

      <div className="flex items-center justify-between gap-8 mb-8 flex-wrap">
        <h1
          id="notes-heading"
          className="text-[28px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-8"
        >
          Customer Notes
        </h1>
        {/* Process-menu navigation (issue #45). Issue #663 (CCRS-3448): when
            Customer Notes was reached FROM a security page's process menu, show
            the SECURITY process menu here (the same cross-navigation set the
            agent came from, excluding the originating page) so they can swivel
            straight back into the security flow — instead of the default
            servicing process menu. Any other entry (servicing menu / direct
            nav) shows the default servicing process menu unchanged. */}
        {fromSecurity ? (
          <SecurityProcessMenu
            currentId={securityReturn.current!.screenId}
            mode={securityReturn.current!.mode}
          />
        ) : (
          <ProcessMenu
            options={PROCESS_MENU_OPTIONS}
            onSelect={onProcessSelect}
          />
        )}
      </div>

      {error && (
        <div
          role="alert"
          className="mb-8 p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
        >
          {error}
        </div>
      )}

      <div className="mb-8 flex flex-wrap gap-4">
        {/* Note options (CCF-49/50): Permanent Notes / Temporary Notes. */}
        <div className="w-[400px]">
          <Dropdown
            id="note-options"
            label="Note options"
            placeholder="Select note option"
            value={noteOption}
            onChange={(value) => {
              setNoteOption(value);
              setSelectedAccount('');
              setRemarks(null);
              setSelected(new Set());
              setShowAdd(false);
              setError('');
              // Issue #630: switching option resets the note-type filter so the
              // permanent view always opens on "All" (show every type).
              setPermanentTypeFilter('');
              if (value === 'permanent') void loadPermanentNotes();
              else setPermanentNotes(null);
            }}
            options={[
              { value: 'permanent', label: 'Permanent Notes' },
              { value: 'temporary', label: 'Temporary Notes' },
            ]}
          />
        </div>

        {/* Permanent note-type filter (issue #630, @philipc39) — revealed
            beside "Note options" once "Permanent Notes" is chosen. The default
            "All note types" shows every note (the page-load view); selecting a
            type filters the displayed permanent notes to that type only. */}
        {showPermanent && (
          <div className="w-[400px]">
            <Dropdown
              id="note-type-filter"
              label="Note type"
              placeholder="All note types"
              value={permanentTypeFilter}
              onChange={setPermanentTypeFilter}
              options={NOTE_TYPE_FILTER_OPTIONS}
            />
          </div>
        )}

        {/* Account selector — revealed once "Temporary Notes" is chosen. */}
        {showTemporary && (
          <div className="w-[400px]">
            <Dropdown
              id="notes-account"
              label="Select account"
              placeholder="Select account"
              value={selectedAccount}
              onChange={onAccountChange}
              options={accounts.map((a) => ({
                value: a.AccountNumber,
                label: accountOptionLabel(a),
              }))}
            />
          </div>
        )}
      </div>

      {/* Permanent notes (CCF-49) — CIF-scoped, read-only, grouped by
          ReferenceNumber. Free text returned "as-is"; no editing. */}
      {showPermanent && (
        <section className="mt-2 max-w-[800px]" aria-labelledby="perm-heading">
          <h2
            id="perm-heading"
            className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-4"
          >
            Permanent notes
          </h2>
          {loading && permanentNotes === null ? (
            <p
              role="status"
              className="text-[16px] font-['Arial',sans-serif] text-[#6b7280] m-0"
            >
              Loading permanent notes…
            </p>
          ) : permanentGroups.length > 0 ? (
            <div className="flex flex-col gap-6">
              {permanentGroups.map(({ noteType, label, refGroups }) => (
                <section key={noteType || 'no-type'} aria-label={label}>
                  {/* Issue #630: note-type heading matches Unibanks screen 49
                      (CUSTOMER NOTES / CUSTOMER COMPLAINT OPEN / etc.). */}
                  <h3 className="text-[13px] font-['Arial',sans-serif] font-bold text-[#1d252c] uppercase tracking-wide mb-3">
                    {label}
                  </h3>
                  <ul className="flex flex-col gap-3 list-none p-0 m-0">
                    {refGroups.map(([noteNumber, notes]) => (
                      // Issue #642: one <li> == one permanent note (grouped by
                      // note number). The date is shown ONCE for the note (all
                      // fragments of a note share the same InputDate); the
                      // fragments are then rendered in sequence order as
                      // consecutive lines of the one note's free text.
                      <li
                        key={noteNumber || 'no-reference'}
                        className="p-4 border border-[#dad7d3] bg-white"
                      >
                        {(noteNumber || notes[0]?.NoteDate) && (
                          <p className="text-[12px] font-['Arial',sans-serif] font-bold text-[#6b7280] m-0 mb-2">
                            {noteNumber ? `Reference ${noteNumber}` : ''}
                            {noteNumber && notes[0]?.NoteDate ? ' — ' : ''}
                            {notes[0]?.NoteDate ?? ''}
                          </p>
                        )}
                        {notes.map((note, i) => (
                          <div
                            key={i}
                            className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] whitespace-pre-wrap"
                          >
                            {note.NoteText}
                          </div>
                        ))}
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          ) : !error && permanentNotes !== null ? (
            // CCF-49 unhappy path — the read succeeded but no notes are on file.
            // Suppressed when the read errored (the error banner shows instead)
            // or has not completed yet, so a hard failure never reads as "empty".
            <p
              role="status"
              className="text-[16px] font-['Arial',sans-serif] text-[#6b7280] m-0"
            >
              No permanent comments are on file.
            </p>
          ) : null}
        </section>
      )}

      {showTemporary && selectedAccount && (
        <div className="mt-2">
          {loading ? (
            <p
              role="status"
              className="text-[16px] font-['Arial',sans-serif] text-[#6b7280]"
            >
              Loading remarks…
            </p>
          ) : remarks ? (
            <>
              {/* Remarks table (CCF-50) with a Select per block (CCF-51). */}
              <div className="mb-4 overflow-x-auto max-w-[800px]">
                <table className="w-full border-collapse bg-white" role="table">
                  <thead>
                    <tr className="bg-[#f3f4f6]">
                      {['Details', 'Account Remarks', 'Select'].map((h) => (
                        <th
                          key={h}
                          className="px-4 py-3 text-left text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] border-b border-[#d1d5db]"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {remarkBlocks.length > 0 ? (
                      remarkBlocks.map((r) => {
                        const id = r.Id ?? '';
                        return (
                          <tr
                            key={id}
                            className="border-b border-[#e5e7eb] hover:bg-[#f9fafb] align-top"
                          >
                            <td className="px-4 py-3 text-[14px] font-['Arial',sans-serif] text-[#1d252c] whitespace-nowrap">
                              {r.Details}
                            </td>
                            <td className="px-4 py-3 text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
                              {(r.Lines ?? []).map((line, i) => (
                                <div key={i}>{line}</div>
                              ))}
                            </td>
                            <td className="px-4 py-3">
                              <input
                                type="checkbox"
                                checked={selected.has(id)}
                                onChange={() => toggleSelected(id)}
                                aria-label={`Select remark ${r.Details ?? id}`}
                                className="w-[18px] h-[18px]"
                              />
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td
                          colSpan={3}
                          className="px-4 py-3 text-[14px] font-['Arial',sans-serif] text-[#6b7280]"
                        >
                          No temporary notes on this account.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Delete (CCF-51) + Add Remark (CCF-56) actions. */}
              <div className="flex flex-wrap items-center gap-4 mb-2">
                <button
                  type="button"
                  disabled={deleting || selected.size === 0}
                  onClick={() => void deleteSelected()}
                  className="px-6 py-3 bg-[#FF703E] text-[#1D252C] rounded-lg text-[16px] font-['Arial',sans-serif] font-bold hover:bg-[#e55a2a] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleting ? 'Working…' : 'Delete'}
                </button>
                {!showAdd && (
                  <button
                    type="button"
                    onClick={() => {
                      if (atLimit) {
                        setLimitError(
                          'The maximum number of remarks allowed on an account has been reached.',
                        );
                        return;
                      }
                      setLimitError('');
                      setShowAdd(true);
                      setAddText('');
                      setAddFieldError('');
                      setAddError('');
                    }}
                    className="px-6 py-3 bg-white text-[#1D252C] border-2 border-[#1D252C] rounded-lg text-[16px] font-['Arial',sans-serif] font-bold hover:bg-[#f5f5f5] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 transition-all"
                  >
                    Add Remark
                  </button>
                )}
              </div>

              {/* Delete failure surfaced INLINE beneath the table (#129). */}
              {deleteError && (
                <p
                  role="alert"
                  className="mb-4 text-[14px] font-['Arial',sans-serif] text-[#dc2626] m-0"
                >
                  {deleteError}
                </p>
              )}
              {limitError && !showAdd && (
                <p
                  role="alert"
                  className="mb-4 text-[14px] font-['Arial',sans-serif] text-[#dc2626] m-0"
                >
                  {limitError} Delete an existing remark to make space.
                </p>
              )}

              {showAdd && (
                <div className="mt-2 max-w-[600px] p-6 border-2 border-[#dad7d3] bg-white">
                  <label
                    htmlFor="remark-text"
                    className="block text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-2"
                  >
                    Add remark (up to {REMARK_LINE_LIMIT} lines; lines wrap
                    automatically at {REMARK_LINE_MAX} characters)
                  </label>
                  <textarea
                    id="remark-text"
                    value={addText}
                    onChange={(e) => {
                      // Auto-wrap at 40 chars/line (CCF-56 / #258): keep typing
                      // past the per-line limit and let overflow flow onto the
                      // next line instead of being blocked.
                      setAddText(wrapRemarkLines(e.target.value));
                      setAddFieldError('');
                      setAddError('');
                    }}
                    rows={4}
                    aria-invalid={!!addFieldError}
                    className={`w-full px-4 py-2 border rounded text-[14px] font-['Arial',sans-serif] text-[#1d252c] placeholder:text-[#9ca3af] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:border-[#005fcc] ${
                      addFieldError ? 'border-[#dc2626]' : 'border-[#979c9f]'
                    }`}
                  />
                  <div className="min-h-[20px]">
                    {addFieldError && (
                      <p
                        className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] m-0"
                        role="alert"
                      >
                        {addFieldError}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-4 mt-2">
                    <button
                      type="button"
                      disabled={adding}
                      onClick={() => void submitAdd()}
                      className="px-6 py-2.5 bg-[#FF703E] text-[#1D252C] rounded-lg text-[14px] font-['Arial',sans-serif] font-bold hover:bg-[#e55a2a] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {adding ? 'Working…' : 'Complete'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowAdd(false);
                        setAddText('');
                        setAddFieldError('');
                        setAddError('');
                      }}
                      className="px-6 py-2.5 bg-white text-[#1D252C] border-2 border-[#1D252C] rounded-lg text-[14px] font-['Arial',sans-serif] font-bold hover:bg-[#f5f5f5] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 transition-all"
                    >
                      Cancel
                    </button>
                  </div>
                  {/* Add failure surfaced INLINE beside Complete (#129). */}
                  {addError && (
                    <p
                      role="alert"
                      className="mt-3 text-[14px] font-['Arial',sans-serif] text-[#dc2626] m-0"
                    >
                      {addError}
                    </p>
                  )}
                </div>
              )}
            </>
          ) : null}
        </div>
      )}
    </main>
  );
}
